import { Box, Button, Divider, Paper, Tooltip, useMediaQuery } from "@mui/material"
import { useCallback, useEffect } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { Location } from 'history'

import { PageTitle } from "src/components/projetos/PageTitle"
import { BottomBar } from "src/framework/components/commons/BottomBar"
import DefaultLayout from "../../edicao/layouts/DefaultLayout";
import theme from "src/theme/light";
import { MAIN_APP_CONTAINER_ID } from "src/framework/layouts/Main";
import { useProjetosHibridoStore } from "src/store/vendas/projetos/hibrido/hibrido.store";
import { isMobileIOS } from "../../utils/deviceUtils";

export interface LayoutProps {
  title: string;
  subtitle?: React.ReactNode;
  onClickBack: () => void;
  onClickNext: () => void;
  nextButtonDisabledTooltip?: string;
  nextButtonIsDisabled?: boolean;
  nextButtonLabel?: string;
}

const mapTipoRedirect = {
  dimensionado: {
    distribuicao_ongrid: "dimensionado_ongrid"
  }
}

const Layout: React.FC<LayoutProps> = ({ children, title, nextButtonLabel = 'Continuar', nextButtonDisabledTooltip = '', subtitle, onClickBack, onClickNext, nextButtonIsDisabled = false }) => {
  const projetosHibridoStoreState = useProjetosHibridoStore(store => store.state.data);
  const projetosHibridoStoreActions = useProjetosHibridoStore(store => store.actions);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const history = useHistory();
  const location = useLocation<{ allowAccess: boolean }>()

  const FooterButton = isMobile ? Box : BottomBar;

  const redirectToStart = useCallback(()=>{
    const oldState = {...projetosHibridoStoreState}
    const tipoRedirect = mapTipoRedirect[oldState.tipo]['distribuicao_ongrid'] || null;
    projetosHibridoStoreActions.reset();

    let url = '/projetos'

    if (tipoRedirect) {
      url += `?tipo=${tipoRedirect}`
    }

    history.replace(url, { done: true })
  },[history, projetosHibridoStoreActions, projetosHibridoStoreState])

  useEffect(()=>{
    // Limpa estado da rota
    window.history.replaceState({}, document.title)
  },[location, location.state])

  useEffect(()=>{
    if (history.location.pathname.includes('edicao') && !projetosHibridoStoreState?.codigo_venda) return;
    if (projetosHibridoStoreState?.isEdit) return;

    // Evita usuário acessar fluxo de criação sem passar pelo formulário inicial..
    const { allowAccess = false } = location.state || {}
    if (!allowAccess) {
      redirectToStart()
    }
  },[history, location, projetosHibridoStoreState?.codigo_venda, projetosHibridoStoreState?.isEdit, redirectToStart])

  useEffect(()=>{
    if (!projetosHibridoStoreState) return;
    if (!projetosHibridoStoreState.tipo) return;
    if (projetosHibridoStoreState.isEdit) {
      const routeTipo = history.location.pathname.split('/')?.[4]
      if (!routeTipo) return;
      if (!routeTipo.includes(projetosHibridoStoreState.tipo)) {
        redirectToStart()
      }
    }
  },[history, history.location.pathname, projetosHibridoStoreState, redirectToStart])
  
  useEffect(()=>{
    const el = document.getElementById(MAIN_APP_CONTAINER_ID)
    if (el) el.scrollIntoView()
  },[title])

  const canNavigate = useCallback((promptLocation: Location | null = null)=>{
    const allowedLocations = ['/novo$', '/edicao$', 'clonar$']
    const loc = (promptLocation || history.location) as Location<{ done: boolean; noReset: boolean }>
    return loc.state?.noReset || loc.state?.done || allowedLocations.some(item => {
      return loc.pathname.match(new RegExp(item))
    })
  },[history.location])

  useEffect(()=>{
    return () => {
      if (!canNavigate()) {
        projetosHibridoStoreActions.reset();
      }
    }
  },[canNavigate, projetosHibridoStoreActions])

  return (
    <DefaultLayout>
      {isMobile ? (<>
        <Box>
          {children}
          <Box style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "10px", padding: "15px", marginTop: "15px", marginBottom: isMobileIOS() ? "100px" : "25px"}}>
            <Button style={{borderRadius: 8}} variant="outlined" color={"secondary"} onClick={onClickBack} fullWidth={isMobile}>Voltar</Button>
            <Button style={{borderRadius: 8}} disableElevation={true} disabled={nextButtonIsDisabled} color={"primary"} variant={"contained"} onClick={onClickNext} fullWidth={isMobile}>{nextButtonLabel}</Button>
          </Box>
        </Box>
      </>): <>
        <Paper>
          <Box p={2}>
            <PageTitle
              title={title}
              subtitle={subtitle || ''}
            />
          </Box>

          <Divider />
          {children}
        </Paper>
        <FooterButton style={isMobile ? { marginTop: 10, padding: 15 } : {}}>
          <Button
            variant="outlined"
            color={"secondary"}
            onClick={onClickBack}
            fullWidth={isMobile}
            style={isMobile ? { marginTop: 15 } : {}}
          >
            Voltar
          </Button>
          <Tooltip title={nextButtonIsDisabled ? nextButtonDisabledTooltip : ''}>
            <span>
              <Button
                disableElevation={true}
                disabled={nextButtonIsDisabled}
                color={"primary"}
                variant={"contained"}
                onClick={onClickNext}
                fullWidth={isMobile}
                style={isMobile ? { marginTop: 15, marginBottom: 15 } : {}}
                >
                {nextButtonLabel}
              </Button>
            </span>
          </Tooltip>
        </FooterButton>

        <Prompt message={(location) => {
          if (canNavigate(location)) return true;
          return `Deseja cancelar a ${projetosHibridoStoreState?.isEdit ? 'edição' : 'criação'} do projeto?`
        }}/>
      </>}
  </DefaultLayout>
  )
}


export { Layout }